import { Injectable, OnDestroy } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { defineUserType } from '../common/app.helpers';
import AppSettings from '../common/app.settings';
import { UserTypes } from '../components/profile/profile.enum';
import { Session } from '../interfaces';
import { AuthorisationSettings, FullUser, LoginResponse } from '../swagger-api';
import { MaybeNull } from '../utils';

@Injectable()
export class SessionService implements OnDestroy {
  private activeSession: Session = {
    token: null,
    user: null,
    userId: null,
    userType: UserTypes.NONE,
    secondAuthLvl: false,
    adminSession: false,
    lastProlongedTime: 0,
  };

  private secondAuthenticationLevelGained$: Subject<void> = new Subject<void>();
  private sessionStartedInOtherTab$: Subject<void> = new Subject<void>();
  private sessionProlongedInOtherTab$: Subject<void> = new Subject<void>();
  private sessionChanges$: ReplaySubject<Session> = new ReplaySubject<Session>();

  private onStorageChangedHandler: (event: StorageEvent) => void;

  public constructor() {
    this.onStorageChangedHandler = this.onStorageChanged.bind(this);
    window.addEventListener('storage', this.onStorageChangedHandler);
  }

  public ngOnDestroy(): void {
    this.sessionChanges$.complete();
    this.sessionStartedInOtherTab$.complete();
    this.sessionProlongedInOtherTab$.complete();
    this.secondAuthenticationLevelGained$.complete();
  }

  public get token(): MaybeNull<string> {
    return localStorage.getItem(AppSettings.LOCALSTORE_KEY_TOKEN);
  }

  public get userId(): MaybeNull<string> {
    return localStorage.getItem(AppSettings.LOCALSTORE_KEY_USERID);
  }

  public set sessionStorageData(response: LoginResponse) {
    const currentTimestamp: number = Date.now();
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_TOKEN, response.token);
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_USERID, response.user_id);
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_TOKEN_PROLONG_TIME, currentTimestamp.toString());
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_ADMIN_SESSION, response.is_admin_session.toString());
    this.activeSession.token = response.token;
    this.activeSession.userId = response.user_id;
    this.activeSession.adminSession = response.is_admin_session;
    this.activeSession.lastProlongedTime = currentTimestamp;
    this.sessionChanges$.next(this.activeSession);
  }

  public get user(): MaybeNull<FullUser> {
    return this.activeSession.user;
  }

  public set user(updatedUser: FullUser) {
    this.activeSession.user = updatedUser;
    this.activeSession.userType = defineUserType(this.user);
    this.notifySessionChanges();
  }

  public get userType(): UserTypes {
    return this.activeSession.userType;
  }

  public set is2AuthLvlPassed(value: boolean) {
    this.activeSession.secondAuthLvl = true;
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_SECONDAUTHLVL, value.toString());
    this.notifySessionChanges();
  }

  public get is2AuthLvlPassed(): boolean {
    return localStorage.getItem(AppSettings.LOCALSTORE_KEY_SECONDAUTHLVL) === 'true';
  }

  public markSessionProlonged(): void {
    const currentTimestamp: number = Date.now();
    this.activeSession.lastProlongedTime = currentTimestamp;
    localStorage.setItem(AppSettings.LOCALSTORE_KEY_TOKEN_PROLONG_TIME, currentTimestamp.toString());
  }

  public notifySessionChanges(): void {
    this.sessionChanges$.next(this.activeSession);
  }

  public get sessionChanges(): Observable<Session> {
    return this.sessionChanges$.asObservable();
  }

  public get secondAuthenticationLevelGained(): Observable<void> {
    return this.secondAuthenticationLevelGained$.asObservable();
  }

  public get sessionStartedInOtherTab(): Observable<void> {
    return this.sessionStartedInOtherTab$.asObservable();
  }

  public get sessionProlongedInOtherTab(): Observable<void> {
    return this.sessionProlongedInOtherTab$.asObservable();
  }

  public get hasNoUserType(): boolean {
    return this.userType === UserTypes.NONE;
  }

  public isIncompleteIndividual(): boolean {
    return this.user.individual ? Object.keys(this.user.individual).length === 0 : false;
  }

  public isSessionActive(): boolean {
    return Boolean(localStorage.getItem(AppSettings.LOCALSTORE_KEY_TOKEN));
  }

  public isAdminSession(): boolean {
    return this.activeSession.adminSession;
  }

  public tryRecoveringSession(): boolean {
    const token: MaybeNull<string> = localStorage.getItem(AppSettings.LOCALSTORE_KEY_TOKEN);
    const userId: MaybeNull<string> = localStorage.getItem(AppSettings.LOCALSTORE_KEY_USERID);
    const adminSession: MaybeNull<string> = localStorage.getItem(AppSettings.LOCALSTORE_KEY_ADMIN_SESSION);

    if (token && userId && adminSession) {
      this.activeSession.token = token;
      this.activeSession.userId = userId;
      this.activeSession.adminSession = adminSession === 'true';
      return true;
    }

    return false;
  }

  public endSession(): void {
    if (this.isSessionActive()) {
      this.clearSession();
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_TOKEN);
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_USERID);
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_ADMIN_SESSION);
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_SECONDAUTHLVL);
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_TOKEN_PROLONG_TIME);
      localStorage.removeItem(AppSettings.LOCALSTORE_KEY_CHAT_WITH_USERID);
      this.sessionChanges$.next(this.activeSession);
    }
  }

  public updateUserAuthorisationSettings(authorisationSettings: AuthorisationSettings): void {
    this.user.authorisation_settings = authorisationSettings;
  }

  private clearSession(): void {
    this.activeSession.token = null;
    this.activeSession.user = null;
    this.activeSession.userId = null;
    this.activeSession.userType = UserTypes.NONE;
    this.activeSession.secondAuthLvl = false;
    this.activeSession.adminSession = false;
  }

  private onStorageChanged(event: StorageEvent): void {
    if (event.storageArea === localStorage) {
      if (event.key === AppSettings.LOCALSTORE_KEY_TOKEN) {
        if (!event.newValue && event.oldValue && this.activeSession.token) {
          this.onExternalLogout();
        } else if (!event.oldValue && event.newValue && !this.activeSession.token) {
          this.onExternalLogin();
        }
      } else if (event.key === AppSettings.LOCALSTORE_KEY_SECONDAUTHLVL) {
        if (event.newValue === 'true' && !this.activeSession.secondAuthLvl) {
          this.onExternalSecondAuthenticationLevelGained();
        }
      } else if (event.key === AppSettings.LOCALSTORE_KEY_TOKEN_PROLONG_TIME) {
        if (event.newValue) {
          const prolongTime: number = parseInt(event.newValue, 10);
          if (prolongTime !== this.activeSession.lastProlongedTime) {
            this.onExternalTokenProlonged(prolongTime);
          }
        }
      }
    }
  }

  private onExternalLogout(): void {
    this.clearSession();
    this.sessionChanges$.next(this.activeSession);
  }

  private onExternalLogin(): void {
    this.sessionStartedInOtherTab$.next();
  }

  private onExternalSecondAuthenticationLevelGained(): void {
    this.activeSession.secondAuthLvl = true;
    this.sessionChanges$.next(this.activeSession);
    this.secondAuthenticationLevelGained$.next();
  }

  private onExternalTokenProlonged(newProlongTime: number): void {
    this.activeSession.lastProlongedTime = newProlongTime;
    this.sessionProlongedInOtherTab$.next();
  }
}
